/**
 * AB Player Shortcode Parser
 * Replaces [abplayer] shortcodes in blog content with native audio players
 * 
 * Usage in blog content:
 * [abplayer before="/path/to/before.mp3" after="/path/to/after.mp3"]
 * [abplayer before="/path/before.mp3" after="/path/after.mp3" color="#0085DD" size="200"]
 * [abplayer type="soundcloud" before="1609922583" beforeSecret="s-xxxx" after="1609922574" afterSecret="s-yyyy"]
 */

class ABPlayerShortcode {
  constructor() {
    this.init();
  }

  init() {
    this.parseContent();
  }

  parseContent() {
    const selectors = ['.dib-post-content', '.blog-post-content', '.rte', 'article .rte', 'rte-formatter'];
    let rteContent = null;
    
    for (const selector of selectors) {
      rteContent = document.querySelector(selector);
      if (rteContent) break;
    }
    
    if (!rteContent) {
      console.warn('ABPlayer: No content container found');
      return;
    }

    const shortcodeRegex = /\[abplayer\s+([^\]]+)\]/gi;
    let counter = 0;
    let html = rteContent.innerHTML;

    // Support editor-authored commented shortcodes by unwrapping them first.
    html = html.replace(/<!--\s*(\[abplayer\s+[^\]]+\])\s*-->/gi, '$1');
    let match = shortcodeRegex.exec(html);

    if (!match) {
      console.log('ABPlayer: No shortcodes found in content');
      return;
    }

    const replacements = [];

    while (match !== null) {
      const fullMatch = match[0];
      const attributes = this.parseAttributes(match[1] || '');

      const validation = this.validateAttributes(attributes);
      if (!validation.valid) {
        console.warn(`ABPlayer: ${validation.reason}`, fullMatch);
        match = shortcodeRegex.exec(html);
        continue;
      }
      
      replacements.push({ fullMatch, native: this.createNativePlayer(attributes) });
      counter++;
      match = shortcodeRegex.exec(html);
    }

    for (const { fullMatch, native } of replacements) {
      html = html.replace(fullMatch, native);
    }

    rteContent.innerHTML = html;
    console.log(`ABPlayer: Replaced ${counter} shortcode(s)`);

    this.loadAssets();
  }

  /** @param {string} attrString */
  parseAttributes(attrString) {
    const attrs = {};

    const typeMatch = attrString.match(/type\s*[:=]\s*["']([^"']+)["']/i);
    const beforeMatch = attrString.match(/before=["']([^"']+)["']/i);
    const afterMatch = attrString.match(/after=["']([^"']+)["']/i);
    const beforeSecretMatch = attrString.match(/beforeSecret=["']([^"']+)["']/i);
    const afterSecretMatch = attrString.match(/afterSecret=["']([^"']+)["']/i);
    const colorMatch = attrString.match(/(?:color|theme)=["']([^"']+)["']/i);
    const sizeMatch = attrString.match(/(?:size|width)=["']?([^"'\s\]]+)["']?/i);

    const typeValue = typeMatch?.[1]?.toLowerCase() || '';
    attrs.sourceType = typeValue === 'soundcloud'
      ? 'soundcloud'
      : 'local';
    if (beforeMatch) attrs.soundBefore = beforeMatch[1];
    if (afterMatch) attrs.soundAfter = afterMatch[1];
    if (beforeSecretMatch) attrs.soundBeforeSecret = beforeSecretMatch[1];
    if (afterSecretMatch) attrs.soundAfterSecret = afterSecretMatch[1];
    if (colorMatch) attrs.color = colorMatch[1];
    if (sizeMatch) attrs.size = this.parseSize(sizeMatch[1]);

    return attrs;
  }

  /** @param {unknown} sizeValue */
  parseSize(sizeValue) {
    const value = String(sizeValue || '').trim().toLowerCase();
    if (!value) return undefined;

    if (/^\d+$/.test(value)) {
      return parseInt(value, 10);
    }

    if (value === 'small') return 140;
    if (value === 'medium') return 200;
    if (value === 'large') return 240;
    return undefined;
  }

  /** @param {{ sourceType?: string; soundBefore?: string; soundAfter?: string }} attrs */
  validateAttributes(attrs) {
    const before = String(attrs.soundBefore || '').trim();
    const after = String(attrs.soundAfter || '').trim();
    if (!before || !after) {
      return { valid: false, reason: 'Missing required attributes (before/after)' };
    }

    if (attrs.sourceType === 'soundcloud') {
      if (!/^\d+$/.test(before) || !/^\d+$/.test(after)) {
        return { valid: false, reason: 'SoundCloud source requires numeric before/after track IDs' };
      }
      return { valid: true };
    }

    if (/^\d+$/.test(before) || /^\d+$/.test(after)) {
      return {
        valid: false,
        reason: 'Mixed source types are not supported; use URLs for local mode or set type="soundcloud" for SoundCloud IDs'
      };
    }

    return { valid: true };
  }

  /** @param {unknown} value */
  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /** @param {{ sourceType?: string; soundBefore?: string; soundAfter?: string; soundBeforeSecret?: string; soundAfterSecret?: string; color?: string; size?: number }} attrs */
  createNativePlayer(attrs) {
    const defaults = {
      sourceType: 'local',
      soundBefore: '',
      soundAfter: '',
      soundBeforeSecret: '',
      soundAfterSecret: '',
      color: '#0085DD',
      size: 200
    };

    const config = { ...defaults, ...attrs };
    const size = Math.max(120, Math.min(400, config.size));

    const beforeUrl = config.sourceType === 'local' ? this.escapeHtml(config.soundBefore) : '';
    const afterUrl = config.sourceType === 'local' ? this.escapeHtml(config.soundAfter) : '';
    const beforeId = config.sourceType === 'soundcloud' ? this.escapeHtml(config.soundBefore) : '';
    const afterId = config.sourceType === 'soundcloud' ? this.escapeHtml(config.soundAfter) : '';
    const beforeSecret = config.sourceType === 'soundcloud' ? this.escapeHtml(config.soundBeforeSecret || '') : '';
    const afterSecret = config.sourceType === 'soundcloud' ? this.escapeHtml(config.soundAfterSecret || '') : '';

    return `<div class="abplayer-native" style="max-width:${size + 40}px;margin:24px auto;">
      <ab-player
        data-source-type="${this.escapeHtml(config.sourceType)}"
        data-before-url="${beforeUrl}"
        data-after-url="${afterUrl}"
        data-before-id="${beforeId}"
        data-after-id="${afterId}"
        data-before-secret="${beforeSecret}"
        data-after-secret="${afterSecret}"
        data-color="${this.escapeHtml(config.color)}"
        data-size="${size}"
        data-label-before="Before"
        data-label-after="After"
        data-label-play="Play"
        data-label-pause="Pause"
      ></ab-player>
    </div>`;
  }

  loadAssets() {
    if (!document.querySelector('link[href*="ab-player.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      const shortcodeScript = /** @type {HTMLScriptElement | null} */ (document.querySelector('script[src*="abplayer-shortcode.js"]'));
      link.href = shortcodeScript
        ?.src.replace('abplayer-shortcode.js', 'ab-player.css') 
        || '/cdn/shop/t/*/assets/ab-player.css';
      document.head.appendChild(link);
    }

    const dynamicImport = new Function('specifier', 'return import(specifier)');
    Promise.resolve(dynamicImport('@theme/ab-player')).catch((e) => {
      console.warn('ABPlayer: Could not load module', e);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ABPlayerShortcode();
});

export { ABPlayerShortcode };
